// A parte em REAIS do Relatório de Créditos: desconto, contribuição, cobranças,
// pagamentos e saldo em aberto.
//
// Separada de `relatorioCreditos.ts` (que cuida de kWh) porque a especificação do
// pacote E4 é explícita sobre não misturar as duas: "separe energia em kWh de valores
// em reais; cálculo, emissão e pagamento são informações distintas". Misturar num
// módulo só convidaria a somar kWh com R$ numa refatoração futura.
//
//   node --experimental-strip-types --test supabase/functions/_shared/__tests__/relatorioCreditosFinanceiro.test.ts
//
// ── As três coisas que NÃO são a mesma ────────────────────────────────────────
//
// CALCULADO é o que a fórmula deu para o ciclo. EMITIDO é o que virou cobrança de
// verdade no gateway. EM ABERTO é o que sobrou depois dos pagamentos e ajustes. A E4
// exige que os três apareçam distintos e que a diferença entre calculado e emitido
// seja exibida "sem ajuste automático" — é por isso que este módulo não concilia
// nada sozinho: ele mostra os dois números e deixa a diferença à vista.
//
// ⚠️ "Valor calculado não é dívida." A frase é do modelo e vale como regra de
// código: nada aqui classifica um valor como vencido sem `vencimento` E cobrança
// emitida. Sem esses dois, a situação é `indefinida`, nunca `vencido`.

import { derivarStatusBoleto, valorDevido, type DadosStatusBoleto } from './statusBoleto.ts';
import { num, numOuNulo, ordemDoMes } from './relatorioCreditos.ts';

/** O que este módulo precisa de cada fatura. Estrutural, como o irmão de energia. */
export interface FaturaFinanceira extends DadosStatusBoleto {
  id?: number | string | null;
  mes_referencia?: string | null;
  data_vencimento?: string | null;

  // Base e desconto (ver src/lib/calculoBoletoVigor.ts — a mesma fórmula).
  custo_s_vigor?: number | string | null;
  consumo_total_gd_s_vigor?: number | string | null;
  valor_adc_bandeira_s_vigor?: number | string | null;
  valor_consumo_scee_boleto?: number | string | null;
  valor_adc_bandeira_boleto?: number | string | null;
  economia?: number | string | null;

  // Deduções — gravadas já negativas pelo cálculo.
  valor_parc_injet_s_desc_boleto?: number | string | null;
  valor_restituicao_imposto_boleto?: number | string | null;
  valor_energia_nao_isenta_boleto?: number | string | null;

  // Acréscimos posteriores, que entram no valor emitido mas não na contribuição do ciclo.
  valor_juros_boleto?: number | string | null;
  outros_multas_boleto?: number | string | null;
  valor_parcelamentos_boleto?: number | string | null;

  // Pagamento e cobrança integrada.
  valor_pagamento?: number | string | null;
  valor_fatura_distribuidora?: number | string | null;

  // Entradas da memória de cálculo.
  qtd_injecao_scee_boleto?: number | string | null;
  energia_injetada_gd1_kwh?: number | string | null;
  energia_injetada_gd2_kwh?: number | string | null;
  valor_tarifa_distribuidora?: number | string | null;
  desconto_tarifa?: number | string | null;
  tarifa_bandeira?: number | string | null;
  desconto_bandeira?: number | string | null;
  consumo_scee_tarifa_c_imposto?: number | string | null;
  tarifa_injecao_gd1?: number | string | null;
  valor_restituicao_imposto?: number | string | null;
}

/** Situação da cobrança do ciclo, na linguagem da E4. */
export type SituacaoCobranca = 'quitada' | 'em_aberto' | 'vencida' | 'a_vencer' | 'cancelada' | 'indefinida';

export interface LinhaFinanceira {
  mesReferencia: string;
  ordem: number | null;
  vencimento: string | null;

  /** Quanto custaria sem a Vigor: quantidade × (tarifa cheia + bandeira cheia). */
  baseAntesDoDesconto: number | null;
  /** Base menos o valor já com desconto. */
  descontoTarifario: number | null;
  /** Parcelas retiradas no cálculo (injeção sem desconto, tributo, energia não isenta). */
  deducoes: number | null;
  /** O que a fórmula apurou para o ciclo, sem acréscimos posteriores. */
  contribuicaoCalculada: number | null;

  /** Juros, multa e parcelamento embutidos na cobrança. */
  acrescimos: number | null;
  /** O que virou cobrança de verdade — `valor_emissao_boleto_unico` quando é boleto único. */
  valorEmitido: number | null;
  pagamentos: number | null;
  saldoEmAberto: number | null;
  situacao: SituacaoCobranca;
  /** Rótulo do sistema (`derivarStatusBoleto`), preservado para quem confere. */
  statusSistema: string;

  /** Parcela da distribuidora, quando a cobrança é integrada (boleto único). */
  parcelaDistribuidora: number | null;
  ehCobrancaIntegrada: boolean;

  /** Falso quando não há cálculo nem cobrança — a linha sai NÃO INFORMADO. */
  temDado: boolean;
}

export interface TotaisFinanceiros {
  descontoTarifario: number;
  contribuicaoCalculada: number;
  totalEmitido: number;
  pagamentos: number;
  saldoEmAberto: number;
  vencido: number;
  aVencer: number;
  /** Quantos ciclos entraram em cada total — a E4 exige identificar conjunto parcial. */
  ciclosComCalculo: number;
  ciclosComCobranca: number;
  ciclosTotal: number;
}

/** Uma entrada da memória de cálculo, na ordem em que o modelo apresenta. */
export interface EntradaMemoria {
  rotulo: string;
  valor: number | null;
  /** Como formatar: quantidade em kWh, tarifa em R$/kWh, percentual ou reais. */
  formato: 'kwh' | 'tarifa' | 'percentual' | 'reais';
}

/** Um passo do quadro "Como chegamos ao valor da referência". */
export interface PassoMemoria {
  formula: string;
  resultado: number | null;
  formato: 'tarifa' | 'reais';
  /** O total final — único valor arredondado, conforme a regra do pacote. */
  total?: boolean;
}

export interface MemoriaCalculo {
  mesReferencia: string;
  entradas: EntradaMemoria[];
  passos: PassoMemoria[];
  /** Falso quando faltam tarifas: o quadro sai como NÃO INFORMADO em vez de zerado. */
  completa: boolean;
}

const r2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100;

/** Soma tratando `null` como ausente, e devolve `null` se NADA chegou — zero e
 *  "não informado" são coisas diferentes neste documento. */
function somaOuNulo(valores: (number | null)[]): number | null {
  const presentes = valores.filter((v): v is number => v !== null);
  return presentes.length ? presentes.reduce((s, v) => s + v, 0) : null;
}

/** Quantidade de energia compensada que entrou no cálculo do ciclo. */
export function quantidadeCalculada(f: FaturaFinanceira): number | null {
  const gd = somaOuNulo([numOuNulo(f.energia_injetada_gd1_kwh), numOuNulo(f.energia_injetada_gd2_kwh)]);
  if (gd !== null && gd > 0) return gd;
  return numOuNulo(f.qtd_injecao_scee_boleto);
}

/**
 * Data que vale para vencimento. `vencimento_boleto` é o da cobrança da Vigor;
 * `data_vencimento` é o da fatura da distribuidora e serve de reserva.
 */
function vencimentoDa(f: FaturaFinanceira): string | null {
  const v = f.vencimento_boleto || f.data_vencimento;
  return v ? String(v) : null;
}

/**
 * A situação da cobrança.
 *
 * ⚠️ Nunca devolve `vencida` sem cobrança emitida E data de vencimento. É a regra
 * do pacote: "se faltar data/base, não classificar o valor como dívida vencida".
 * `hoje` entra por parâmetro para o teste não depender do relógio.
 */
export function situacaoDaCobranca(f: FaturaFinanceira, hoje: Date = new Date()): SituacaoCobranca {
  const status = derivarStatusBoleto(f);
  if (status === 'Pago' || status === 'Quitado') return 'quitada';
  if (status === 'Cancelado') return 'cancelada';

  const emitido = valorDevido(f);
  const venc = vencimentoDa(f);
  if (!emitido || !venc) return 'indefinida';

  // Comparação por string ISO: converter para Date leria a DATE como UTC e, no
  // horário de Brasília, uma cobrança que vence hoje apareceria vencida ontem.
  const hojeIso = hoje.toISOString().slice(0, 10);
  const vencIso = String(venc).slice(0, 10);
  if (status === 'Vencido') return 'vencida';
  return vencIso < hojeIso ? 'vencida' : 'a_vencer';
}

export function linhaFinanceiraDaFatura(f: FaturaFinanceira, hoje?: Date): LinhaFinanceira {
  const base = numOuNulo(f.custo_s_vigor)
    ?? somaOuNulo([numOuNulo(f.consumo_total_gd_s_vigor), numOuNulo(f.valor_adc_bandeira_s_vigor)]);

  const aposDesconto = somaOuNulo([
    numOuNulo(f.valor_consumo_scee_boleto),
    numOuNulo(f.valor_adc_bandeira_boleto),
  ]);

  // `economia` já é base − após desconto no cálculo do sistema; recalcular aqui
  // daria o mesmo número, e usar o gravado mantém o relatório igual ao boleto.
  const desconto = numOuNulo(f.economia)
    ?? (base !== null && aposDesconto !== null ? r2(base - aposDesconto) : null);

  // Gravadas negativas pelo cálculo. O relatório mostra o quanto foi retirado,
  // então entra em módulo — um "desconto de -R$ 30" confundiria o associado.
  const deducoes = somaOuNulo([
    numOuNulo(f.valor_parc_injet_s_desc_boleto),
    numOuNulo(f.valor_restituicao_imposto_boleto),
    numOuNulo(f.valor_energia_nao_isenta_boleto),
  ]);

  const acrescimos = somaOuNulo([
    numOuNulo(f.valor_juros_boleto),
    numOuNulo(f.outros_multas_boleto),
    numOuNulo(f.valor_parcelamentos_boleto),
  ]);

  const valorVigor = numOuNulo(f.valor_boleto_vigor);
  // A contribuição do CICLO exclui juros/multa/parcelamento: esses são eventos
  // posteriores que entraram na mesma cobrança, não o valor apurado do mês.
  //
  // ⚠️ Exige a base: sem ela não houve cálculo, e `valor_boleto_vigor` sozinho é o
  // valor EMITIDO — apresentá-lo como "contribuição calculada" apagaria justamente
  // a distinção que o pacote manda preservar ("valor calculado, emitido e em aberto
  // devem permanecer distintos"). Acontece em fatura de acordo e em cobrança avulsa,
  // que têm valor mas nenhuma memória por trás.
  const contribuicao = valorVigor === null || base === null
    ? null
    : r2(valorVigor - (acrescimos ?? 0));

  const emitidoBruto = valorDevido(f);
  const valorEmitido = emitidoBruto > 0 ? r2(emitidoBruto) : null;
  const pagamentos = numOuNulo(f.valor_pagamento);

  const situacao = situacaoDaCobranca(f, hoje);
  const saldoEmAberto = valorEmitido === null
    ? null
    : situacao === 'quitada' || situacao === 'cancelada'
      ? 0
      : r2(valorEmitido - (pagamentos ?? 0));

  const unico = numOuNulo(f.valor_emissao_boleto_unico);
  const ehCobrancaIntegrada = unico !== null && unico > 0;

  return {
    mesReferencia: String(f.mes_referencia ?? '').trim(),
    ordem: ordemDoMes(f.mes_referencia),
    vencimento: vencimentoDa(f),
    baseAntesDoDesconto: base === null ? null : r2(base),
    descontoTarifario: desconto === null ? null : r2(desconto),
    deducoes: deducoes === null ? null : r2(Math.abs(deducoes)),
    contribuicaoCalculada: contribuicao,
    acrescimos: acrescimos === null ? null : r2(acrescimos),
    valorEmitido,
    pagamentos: pagamentos === null ? null : r2(pagamentos),
    saldoEmAberto,
    situacao,
    statusSistema: derivarStatusBoleto(f),
    // Na cobrança integrada, a parcela da distribuidora é o que sobra do total do
    // documento depois da contribuição da Vigor.
    parcelaDistribuidora: ehCobrancaIntegrada && valorVigor !== null
      ? r2(unico! - valorVigor)
      : numOuNulo(f.valor_fatura_distribuidora),
    ehCobrancaIntegrada,
    temDado: base !== null || valorEmitido !== null,
  };
}

export interface RelatorioFinanceiro {
  linhas: LinhaFinanceira[];
  totais: TotaisFinanceiros;
  /** Memória do ciclo mais recente com cálculo completo — o "Exemplo simples". */
  memoria: MemoriaCalculo | null;
  /** Cobranças que reúnem contribuição Vigor e parcela da distribuidora. */
  temCobrancaIntegrada: boolean;
}

/**
 * Percentual gravado ora como fração (0,18) ora como inteiro (18).
 *
 * `desconto_tarifa` entra na fórmula como `1 - desconto`, então o banco guarda
 * fração; mas o campo é editável na tela de validação e já apareceu preenchido
 * como 18. Acima de 1 só pode ser percentual — desconto de 180% não existe.
 */
export function percentualDesconto(v: unknown): number | null {
  const n = numOuNulo(v);
  if (n === null) return null;
  return n > 1 ? n : r2(n * 100);
}

/** A memória do "Exemplo simples de funcionamento", no formato do modelo. */
export function memoriaDaFatura(f: FaturaFinanceira): MemoriaCalculo {
  const qtd = quantidadeCalculada(f);
  const tarifaEnergia = numOuNulo(f.valor_tarifa_distribuidora);
  const descEnergia = percentualDesconto(f.desconto_tarifa);
  const tarifaBandeira = numOuNulo(f.tarifa_bandeira);
  const descBandeira = percentualDesconto(f.desconto_bandeira);
  const tarifaConsumoScee = numOuNulo(f.consumo_scee_tarifa_c_imposto);
  const tarifaInjecaoScee = numOuNulo(f.tarifa_injecao_gd1);
  const tributo = numOuNulo(f.valor_restituicao_imposto);

  const entradas: EntradaMemoria[] = [
    { rotulo: 'Quantidade efetivamente utilizada', valor: qtd, formato: 'kwh' },
    { rotulo: 'Tarifa de energia sem o desconto Vigor', valor: tarifaEnergia, formato: 'tarifa' },
    { rotulo: 'Desconto sobre a energia', valor: descEnergia, formato: 'percentual' },
    { rotulo: 'Tarifa da bandeira', valor: tarifaBandeira, formato: 'tarifa' },
    { rotulo: 'Desconto sobre a bandeira', valor: descBandeira, formato: 'percentual' },
    { rotulo: 'Tarifa do campo Consumo SCEE', valor: tarifaConsumoScee, formato: 'tarifa' },
    { rotulo: 'Tarifa do campo Injeção SCEE', valor: tarifaInjecaoScee, formato: 'tarifa' },
    { rotulo: 'Tributo da compensação deduzido', valor: numOuNulo(f.valor_restituicao_imposto), formato: 'reais' },
  ];

  const linha = linhaFinanceiraDaFatura(f);
  const fator = (d: number | null) => (d === null ? null : 1 - d / 100);
  const tarifaEnergiaComDesconto = tarifaEnergia !== null && descEnergia !== null
    ? tarifaEnergia * fator(descEnergia)! : null;
  const tarifaBandeiraComDesconto = tarifaBandeira !== null && descBandeira !== null
    ? tarifaBandeira * fator(descBandeira)! : null;

  const passos: PassoMemoria[] = [
    {
      formula: 'Tarifa de energia × (1 − desconto da energia ÷ 100)',
      resultado: tarifaEnergiaComDesconto,
      formato: 'tarifa',
    },
    {
      formula: 'Tarifa da bandeira × (1 − desconto da bandeira ÷ 100)',
      resultado: tarifaBandeiraComDesconto,
      formato: 'tarifa',
    },
    {
      formula: 'Base antes do desconto = quantidade × (tarifa de energia + tarifa da bandeira)',
      resultado: linha.baseAntesDoDesconto,
      formato: 'reais',
    },
    {
      formula: 'Após desconto = quantidade × (tarifa de energia com desconto + tarifa da bandeira com desconto)',
      resultado: somaOuNulo([numOuNulo(f.valor_consumo_scee_boleto), numOuNulo(f.valor_adc_bandeira_boleto)]),
      formato: 'reais',
    },
    {
      formula: 'Desconto em reais = base antes do desconto − valor após desconto',
      resultado: linha.descontoTarifario,
      formato: 'reais',
    },
    {
      formula: 'Dedução da compensação = (tarifa Consumo SCEE − tarifa Injeção SCEE) × quantidade',
      resultado: numOuNulo(f.valor_parc_injet_s_desc_boleto) === null
        ? null
        : r2(Math.abs(num(f.valor_parc_injet_s_desc_boleto))),
      formato: 'reais',
    },
    {
      formula: 'Contribuição antes de arredondar = valor após desconto − tributo deduzido − dedução da compensação',
      resultado: linha.contribuicaoCalculada,
      formato: 'reais',
    },
    {
      formula: 'Total final da cobrança, após consolidar as memórias e arredondar para duas casas',
      resultado: linha.valorEmitido ?? linha.contribuicaoCalculada,
      formato: 'reais',
      total: true,
    },
  ];

  // A memória só serve de exemplo se chegar até o fim. Exigir a contribuição além
  // da base não é preciosismo: sem ela, as duas últimas linhas do quadro — as que
  // respondem "de onde saiu o valor que estou pagando" — sairiam NÃO INFORMADO, e
  // um exemplo que não chega ao valor final não explica nada.
  const completa = qtd !== null
    && tarifaEnergia !== null
    && linha.baseAntesDoDesconto !== null
    && linha.contribuicaoCalculada !== null;

  return { mesReferencia: String(f.mes_referencia ?? '').trim(), entradas, passos, completa };
}

/**
 * O bloco financeiro inteiro.
 *
 * `faturas` já vem deduplicada por mês pelo chamador — a dedup é a mesma decisão
 * tomada do lado da energia, e refazê-la aqui com outro critério faria a seção 6
 * falar de um ciclo e a seção 4, de outro.
 */
export function montarRelatorioFinanceiro(
  faturas: FaturaFinanceira[],
  hoje?: Date
): RelatorioFinanceiro {
  const linhas = faturas
    .map(f => linhaFinanceiraDaFatura(f, hoje))
    .sort((a, b) => {
      if (a.ordem === null && b.ordem === null) return a.mesReferencia.localeCompare(b.mesReferencia);
      if (a.ordem === null) return 1;
      if (b.ordem === null) return -1;
      return a.ordem - b.ordem;
    });

  const comCalculo = linhas.filter(l => l.contribuicaoCalculada !== null);
  const comCobranca = linhas.filter(l => l.valorEmitido !== null);

  const totais: TotaisFinanceiros = {
    descontoTarifario: r2(linhas.reduce((s, l) => s + (l.descontoTarifario ?? 0), 0)),
    contribuicaoCalculada: r2(comCalculo.reduce((s, l) => s + (l.contribuicaoCalculada ?? 0), 0)),
    totalEmitido: r2(comCobranca.reduce((s, l) => s + (l.valorEmitido ?? 0), 0)),
    pagamentos: r2(linhas.reduce((s, l) => s + (l.pagamentos ?? 0), 0)),
    saldoEmAberto: r2(comCobranca.reduce((s, l) => s + (l.saldoEmAberto ?? 0), 0)),
    vencido: r2(comCobranca.filter(l => l.situacao === 'vencida').reduce((s, l) => s + (l.saldoEmAberto ?? 0), 0)),
    aVencer: r2(comCobranca.filter(l => l.situacao === 'a_vencer').reduce((s, l) => s + (l.saldoEmAberto ?? 0), 0)),
    ciclosComCalculo: comCalculo.length,
    ciclosComCobranca: comCobranca.length,
    ciclosTotal: linhas.length,
  };

  // A memória de exemplo é a do ciclo mais recente que tem cálculo completo: é o
  // que o associado acabou de receber e sobre o que ele liga perguntando.
  let memoria: MemoriaCalculo | null = null;
  for (const f of faturas) {
    const m = memoriaDaFatura(f);
    if (!m.completa) continue;
    const atual = ordemDoMes(m.mesReferencia) ?? -1;
    const anterior = memoria ? (ordemDoMes(memoria.mesReferencia) ?? -1) : -Infinity;
    if (atual >= anterior) memoria = m;
  }

  return {
    linhas,
    totais,
    memoria,
    temCobrancaIntegrada: linhas.some(l => l.ehCobrancaIntegrada),
  };
}

/** A marcação de dado ausente do pacote. Nunca substituir por zero. */
export const NAO_INFORMADO = 'NÃO INFORMADO';

export function formatarReaisOuNI(n: number | null | undefined): string {
  if (n === null || n === undefined) return NAO_INFORMADO;
  return `R$ ${n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Tarifa tem mais casas que dinheiro: R$ 0,89 esconde a diferença entre 0,8912 e
 *  0,8949, e é essa diferença que o associado usa para refazer a conta. */
export function formatarTarifa(n: number | null | undefined): string {
  if (n === null || n === undefined) return NAO_INFORMADO;
  return `R$ ${n.toLocaleString('pt-BR', { minimumFractionDigits: 6, maximumFractionDigits: 6 })}`;
}

export function formatarPercentual(n: number | null | undefined): string {
  if (n === null || n === undefined) return NAO_INFORMADO;
  return `${n.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
}

export const ROTULO_SITUACAO: Record<SituacaoCobranca, string> = {
  quitada: 'Quitada',
  em_aberto: 'Em aberto',
  vencida: 'Vencida',
  a_vencer: 'A vencer',
  cancelada: 'Cancelada',
  indefinida: NAO_INFORMADO,
};
